import React from 'react';
import styled from 'styled-components';
import { FiPlus, FiLayers, FiZap, FiX, FiFileText, FiHelpCircle, FiUpload } from 'react-icons/fi';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: #374151;
  }
`;

const ModalTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ModalDescription = styled.p`
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.5;
  text-align: center;
  font-size: 10px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
`;

const OptionCard = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
})`
  border: 2px solid ${props => props.selected ? '#1f2937' : '#e5e7eb'};
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#f9fafb' : 'white'};
  
  &:hover {
    border-color: ${props => props.selected ? '#1f2937' : '#9ca3af'};
    background: ${props => props.selected ? '#f9fafb' : '#f9fafb'};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const OptionIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
})`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.selected ? '#1f2937' : '#f3f4f6'};
  color: ${props => props.selected ? 'white' : '#6b7280'};
  font-size: 24px;
  transition: all 0.2s ease;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const OptionDescription = styled.p`
  font-size: 10px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.variant === 'primary' ? `
    background: #1f2937;
    color: white;
    border: none;
    
    &:hover {
      background: #374151;
    }
    
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      color: #374151;
    }
  `}
`;

function CreateFormModal({ isOpen, onClose, onSelectOption }) {
  const [step, setStep] = React.useState('type'); // 'type' or 'method'
  const [formType, setFormType] = React.useState(null); // 'survey' or 'questionnaire'
  const [selectedOption, setSelectedOption] = React.useState('scratch');

  const handleSelectType = (type) => {
    setFormType(type);
    if (type === 'questionnaire') {
      // Directly go to questionnaire builder
      onSelectOption('questionnaire');
      onClose();
      resetModal();
    } else {
      // Show creation method options for survey
      setStep('method');
    }
  };

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    
    // Call the parent handler first
    onSelectOption(option);
    
    // For AI and upload options, the parent (Dashboard) will handle closing the modal
    // For other options, close immediately
    if (option !== 'ai' && option !== 'upload') {
      onClose();
      resetModal();
    } else {
      // Reset the modal state but don't close (parent will handle it)
      resetModal();
    }
  };

  const handleBack = () => {
    setStep('type');
    setSelectedOption('scratch');
  };

  const resetModal = () => {
    setStep('type');
    setFormType(null);
    setSelectedOption('scratch');
  };

  if (!isOpen) return null;

  const typeOptions = [
    {
      id: 'survey',
      title: 'Survey',
      description: 'Create a survey form to collect responses and feedback from users',
      icon: FiFileText,
      color: '#10b981'
    },
    {
      id: 'questionnaire',
      title: 'Questionnaire',
      description: 'Create a quiz or assessment with correct answers and automatic grading (redirects immediately)',
      icon: FiHelpCircle,
      color: '#8b5cf6'
    }
  ];

  const methodOptions = [
    {
      id: 'scratch',
      title: 'Create from Scratch',
      description: 'Start with a blank form and build it step by step with full customization (redirects immediately)',
      icon: FiPlus,
      color: '#10b981'
    },
    {
      id: 'ai',
      title: 'AI Generator',
      description: 'Use AI to generate a form automatically from a text description or upload a Word document',
      icon: FiZap,
      color: '#f59e0b'
    },
    {
      id: 'template',
      title: 'Use Template',
      description: 'Choose from existing templates to get started quickly with pre-built forms (redirects immediately)',
      icon: FiLayers,
      color: '#3b82f6'
    },
    {
      id: 'upload',
      title: 'Upload Word File',
      description: 'Upload a Word document and automatically convert it into a survey form (redirects immediately)',
      icon: FiUpload,
      color: '#8b5cf6'
    }
  ];

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>
        
        {step === 'type' ? (
          <>
            <ModalTitle>Create New Form</ModalTitle>
            
            <ModalDescription>
              Choose the type of form you'd like to create:
            </ModalDescription>
            
            <OptionsGrid>
              {typeOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <OptionCard 
                    key={option.id}
                    selected={formType === option.id}
                    onClick={() => handleSelectType(option.id)}
                  >
                    <OptionHeader>
                      <OptionIcon selected={formType === option.id}>
                        <IconComponent />
                      </OptionIcon>
                      <OptionContent>
                        <OptionTitle>{option.title}</OptionTitle>
                        <OptionDescription>{option.description}</OptionDescription>
                      </OptionContent>
                    </OptionHeader>
                  </OptionCard>
                );
              })}
            </OptionsGrid>
            
            <ButtonContainer>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </ButtonContainer>
          </>
        ) : (
          <>
            <ModalTitle>Create Survey</ModalTitle>
            
            <ModalDescription>
              Choose how you'd like to create your survey:
            </ModalDescription>
            
            <OptionsGrid>
              {methodOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <OptionCard 
                    key={option.id}
                    selected={selectedOption === option.id}
                    onClick={() => handleSelectOption(option.id)}
                  >
                    <OptionHeader>
                      <OptionIcon selected={selectedOption === option.id}>
                        <IconComponent />
                      </OptionIcon>
                      <OptionContent>
                        <OptionTitle>{option.title}</OptionTitle>
                        <OptionDescription>{option.description}</OptionDescription>
                      </OptionContent>
                    </OptionHeader>
                  </OptionCard>
                );
              })}
            </OptionsGrid>
            
            <ButtonContainer>
              <Button variant="secondary" onClick={handleBack}>
                Back
              </Button>
            </ButtonContainer>
          </>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
}

export default CreateFormModal;
